<script setup>
// NOTICE: KAPTION-INTEGRATION — Leaderboard of top campaigns by send volume.
// Rows are PRE-AGGREGATED from the wanalytics `get_campaigns` endpoint
// (Kaption::AnalyticsDataService). Rates are computed for display only.
//
// Interactions:
//   • Clicking a row toggles that campaign as a dashboard filter (`toggleFilter`),
//     re-scoping every metric to the selection — reuses the existing campaign_ids
//     plumbing. Selected rows are highlighted with a check.
//   • The eye trigger opens a WhatsApp template preview popover (a single warm
//     iframe, retargeted per row). Hover to peek, click to pin.
//   • The ↗ button opens the full single-campaign report (`open`).
import { computed, ref, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import KaptionTemplatePreviewPopover from './KaptionTemplatePreviewPopover.vue';

const props = defineProps({
  // Array of { id, title, campaign_type, sent, delivered, read, replied, failed, template? }
  campaigns: {
    type: Array,
    default: () => [],
  },
  limit: {
    type: Number,
    default: 10,
  },
  // Campaign ids currently active as filters — highlighted + checked.
  selectedIds: {
    type: Array,
    default: () => [],
  },
  // Optional async resolver `(campaignId) => template|null` for lazy-loading the
  // template to preview. Defaults to the inline `campaign.template` (story/tests).
  resolveTemplate: {
    type: Function,
    default: null,
  },
});

const emit = defineEmits(['toggleFilter', 'open']);

const { t } = useI18n();

const pct = (numerator, denominator) => {
  if (!denominator) return 0;
  return Math.round((numerator / denominator) * 1000) / 10;
};

// Already pre-sorted by the API, but defensively sort + slice for the leaderboard.
const rows = computed(() =>
  [...props.campaigns]
    .sort((a, b) => (b.sent ?? 0) - (a.sent ?? 0))
    .slice(0, props.limit)
    .map(c => ({
      id: c.id,
      title: c.title,
      isDirect: c.campaign_type === 'direct',
      sent: c.sent ?? 0,
      deliveryRate: pct(c.delivered, c.sent),
      readRate: pct(c.read, c.sent),
      replyRate: pct(c.replied, c.sent),
      // NOTICE: KAPTION-INTEGRATION — clicks + per-link breakdown tooltip
      clicked: c.clicked ?? 0,
      clicksByLink: c.clicks_by_link || c.clicksByLink || [],
    }))
);

const hasData = computed(() => rows.value.length > 0);

// NOTICE: KAPTION-INTEGRATION — which-link labels for the clicks tooltip
const linkSlotLabel = slot => {
  if (!slot) return t('CAMPAIGN.REPORT.CLICKS_BY_LINK.UNKNOWN_LINK');
  const btn = slot.match(/^button\[(\d+)\]$/);
  if (btn) {
    return t('CAMPAIGN.REPORT.CLICKS_BY_LINK.BUTTON_N', {
      n: Number(btn[1]) + 1,
    });
  }
  if (/^body\[/.test(slot))
    return t('CAMPAIGN.REPORT.CLICKS_BY_LINK.BODY_LINK');
  return slot;
};

const selectedSet = computed(
  () => new Set(props.selectedIds.map(id => Number(id)))
);
const isSelected = id => selectedSet.value.has(Number(id));

// --- Row interactions -------------------------------------------------------
const onRowClick = row => emit('toggleFilter', row.id);
const onOpen = (row, event) => {
  event.stopPropagation();
  emit('open', row.id);
};

// --- Template preview popover (single warm iframe, retargeted on demand) -----
const previewId = ref(null);
const previewAnchor = ref(null);
const previewTitle = ref('');
const previewTemplate = ref(null);
const previewLoading = ref(false);
const previewError = ref(false);
const previewPinned = ref(false);

const previewVisible = computed(() => previewId.value !== null);

const templateCache = new Map();
let openTimer = null;
let closeTimer = null;

const resolveFor = async id => {
  if (templateCache.has(id)) return templateCache.get(id);
  let tpl = null;
  if (props.resolveTemplate) {
    tpl = await props.resolveTemplate(id);
  } else {
    tpl = props.campaigns.find(c => c.id === id)?.template ?? null;
  }
  templateCache.set(id, tpl ?? null);
  return tpl ?? null;
};

const showPreview = async (row, el, { pinned = false } = {}) => {
  clearTimeout(closeTimer);
  previewAnchor.value = el.getBoundingClientRect();
  previewTitle.value = row.title;
  if (pinned) previewPinned.value = true;
  // Same campaign already shown — just keep it (and apply pin).
  if (previewId.value === row.id) return;

  previewId.value = row.id;
  previewLoading.value = true;
  previewError.value = false;
  previewTemplate.value = null;
  const id = row.id;
  try {
    const tpl = await resolveFor(id);
    if (previewId.value !== id) return; // user moved on
    previewTemplate.value = tpl;
    previewError.value = !tpl;
  } catch {
    if (previewId.value !== id) return;
    previewTemplate.value = null;
    previewError.value = true;
  } finally {
    if (previewId.value === id) previewLoading.value = false;
  }
};

const closePreview = () => {
  previewId.value = null;
  previewPinned.value = false;
};

const onPreviewEnter = (row, event) => {
  clearTimeout(openTimer);
  const el = event.currentTarget;
  openTimer = setTimeout(() => showPreview(row, el), 200);
};

const onPreviewLeave = () => {
  clearTimeout(openTimer);
  if (previewPinned.value) return;
  closeTimer = setTimeout(() => {
    if (!previewPinned.value) closePreview();
  }, 150);
};

const onPreviewClick = (row, event) => {
  event.stopPropagation();
  clearTimeout(openTimer);
  clearTimeout(closeTimer);
  const el = event.currentTarget;
  if (previewId.value === row.id && previewPinned.value) {
    closePreview();
  } else {
    showPreview(row, el, { pinned: true });
  }
};

const onPopoverEnter = () => clearTimeout(closeTimer);
const onPopoverLeave = () => {
  if (previewPinned.value) return;
  closeTimer = setTimeout(() => {
    if (!previewPinned.value) closePreview();
  }, 150);
};

onBeforeUnmount(() => {
  clearTimeout(openTimer);
  clearTimeout(closeTimer);
});
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-baseline justify-between">
      <h3 class="text-sm font-semibold text-n-slate-12">
        {{ t('CAMPAIGN.REPORT.LEADERBOARD.TITLE') }}
      </h3>
      <span class="text-xs text-n-slate-10">
        {{ t('CAMPAIGN.REPORT.LEADERBOARD.SUBTITLE') }}
      </span>
    </div>

    <div v-if="hasData" class="rounded-xl border border-n-weak overflow-hidden">
      <table class="w-full text-sm">
        <thead>
          <tr class="bg-n-alpha-black2">
            <th
              class="px-3 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide w-10"
            >
              {{ t('CAMPAIGN.REPORT.LEADERBOARD.RANK') }}
            </th>
            <th
              class="px-3 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ t('CAMPAIGN.REPORT.LEADERBOARD.NAME') }}
            </th>
            <th
              class="px-3 py-3 text-left text-xs font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ t('CAMPAIGN.REPORT.LEADERBOARD.TYPE') }}
            </th>
            <th
              class="px-3 py-3 text-right text-xs font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ t('CAMPAIGN.REPORT.LEADERBOARD.SENT') }}
            </th>
            <th
              class="px-3 py-3 text-right text-xs font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ t('CAMPAIGN.REPORT.LEADERBOARD.DELIVERY_RATE') }}
            </th>
            <th
              class="px-3 py-3 text-right text-xs font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ t('CAMPAIGN.REPORT.LEADERBOARD.READ_RATE') }}
            </th>
            <th
              class="px-3 py-3 text-right text-xs font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ t('CAMPAIGN.REPORT.LEADERBOARD.REPLY_RATE') }}
            </th>
            <th
              class="px-3 py-3 text-right text-xs font-medium text-n-slate-10 uppercase tracking-wide"
            >
              {{ t('CAMPAIGN.REPORT.METRICS.CLICKS') }}
            </th>
            <th class="w-10 px-3 py-3" aria-hidden="true" />
          </tr>
        </thead>
        <tbody class="divide-y divide-n-weak">
          <tr
            v-for="(row, idx) in rows"
            :key="row.id"
            class="transition-colors cursor-pointer"
            :class="
              isSelected(row.id)
                ? 'bg-n-teal-3 hover:bg-n-teal-4'
                : 'hover:bg-n-alpha-black2'
            "
            :aria-selected="isSelected(row.id)"
            data-testid="campaign-row"
            @click="onRowClick(row)"
          >
            <td class="px-3 py-3 text-n-slate-10 font-semibold tabular-nums">
              <span
                v-if="isSelected(row.id)"
                class="i-lucide-check w-4 h-4 text-n-teal-11"
                data-testid="row-selected-check"
              />
              <span v-else>{{ idx + 1 }}</span>
            </td>
            <td class="px-3 py-3 text-n-slate-12 font-medium">
              <span class="inline-flex items-center gap-2">
                <button
                  type="button"
                  class="i-lucide-eye w-4 h-4 text-n-slate-9 hover:text-n-teal-11 shrink-0"
                  :aria-label="t('CAMPAIGN.REPORT.LEADERBOARD.PREVIEW')"
                  :title="t('CAMPAIGN.REPORT.LEADERBOARD.PREVIEW')"
                  data-testid="preview-trigger"
                  @click="onPreviewClick(row, $event)"
                  @mouseenter="onPreviewEnter(row, $event)"
                  @mouseleave="onPreviewLeave"
                />
                {{ row.title }}
              </span>
            </td>
            <td class="px-3 py-3">
              <span
                class="px-2 py-0.5 text-xs font-medium rounded-full"
                :class="
                  row.isDirect
                    ? 'bg-n-teal-4 text-n-teal-11'
                    : 'bg-n-slate-4 text-n-slate-11'
                "
              >
                {{
                  row.isDirect
                    ? t('CAMPAIGN.REPORT.LEADERBOARD.TYPE_DIRECT')
                    : t('CAMPAIGN.REPORT.LEADERBOARD.TYPE_CAMPAIGN')
                }}
              </span>
            </td>
            <td
              class="px-3 py-3 text-right text-n-slate-12 font-semibold tabular-nums"
            >
              {{ row.sent.toLocaleString() }}
            </td>
            <td class="px-3 py-3 text-right text-n-teal-11 tabular-nums">
              {{ row.deliveryRate }}%
            </td>
            <td class="px-3 py-3 text-right text-n-teal-11 tabular-nums">
              {{ row.readRate }}%
            </td>
            <td class="px-3 py-3 text-right text-n-green-11 tabular-nums">
              {{ row.replyRate }}%
            </td>
            <!-- NOTICE: KAPTION-INTEGRATION — clicks + which-link hover tooltip -->
            <td class="px-3 py-3 text-right tabular-nums">
              <span
                v-if="row.clicked > 0"
                class="relative group inline-block font-semibold text-n-green-11"
                :class="row.clicksByLink.length ? 'cursor-help' : ''"
              >
                {{ row.clicked.toLocaleString() }}
                <span
                  v-if="row.clicksByLink.length"
                  class="absolute bottom-full right-0 mb-1.5 z-30 hidden group-hover:block min-w-56 rounded-lg border border-n-weak bg-n-solid-1 shadow-xl px-3 py-2 text-left font-sans"
                >
                  <span
                    class="block text-[10px] font-semibold text-n-slate-10 uppercase tracking-wide mb-1"
                  >
                    {{ t('CAMPAIGN.REPORT.CLICKS_BY_LINK.TITLE') }}
                  </span>
                  <span
                    v-for="(link, li) in row.clicksByLink"
                    :key="li"
                    class="flex items-center justify-between gap-4 py-1 border-t border-n-weak first:border-t-0"
                    :class="!link.link_slot ? 'opacity-60' : ''"
                  >
                    <span class="min-w-0">
                      <span class="block text-xs font-semibold text-n-slate-12">
                        {{ linkSlotLabel(link.link_slot) }}
                      </span>
                      <span
                        v-if="link.destination"
                        class="block text-[11px] text-n-slate-10 truncate max-w-44"
                      >
                        {{ link.destination }}
                      </span>
                    </span>
                    <span class="text-xs font-bold text-n-green-11">
                      {{ link.clicks }}
                    </span>
                  </span>
                </span>
              </span>
              <span v-else class="text-n-slate-10">0</span>
            </td>
            <td class="px-3 py-3 text-right">
              <button
                type="button"
                class="i-lucide-arrow-up-right w-4 h-4 text-n-slate-9 hover:text-n-slate-12"
                :aria-label="t('CAMPAIGN.REPORT.LEADERBOARD.OPEN_REPORT')"
                :title="t('CAMPAIGN.REPORT.LEADERBOARD.OPEN_REPORT')"
                data-testid="open-report"
                @click="onOpen(row, $event)"
              />
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-else
      class="flex flex-col items-center justify-center py-12 gap-2 rounded-xl border border-n-weak bg-n-alpha-black2"
    >
      <span class="i-lucide-trophy w-8 h-8 text-n-slate-8" />
      <span class="text-sm text-n-slate-10">
        {{ t('CAMPAIGN.REPORT.LEADERBOARD.NO_DATA') }}
      </span>
    </div>

    <p v-if="hasData" class="text-xs text-n-slate-9">
      {{ t('CAMPAIGN.REPORT.LEADERBOARD.HINT') }}
    </p>

    <KaptionTemplatePreviewPopover
      :visible="previewVisible"
      :title="previewTitle"
      :template="previewTemplate"
      :loading="previewLoading"
      :error="previewError"
      :anchor="previewAnchor"
      :pinned="previewPinned"
      @mouseenter="onPopoverEnter"
      @mouseleave="onPopoverLeave"
      @close="closePreview"
    />
  </div>
</template>
